import {
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Box,
  Button
} from '@mui/material'
import { Asset } from '../types'
import { useTranslation } from 'react-i18next'
import { useDialog, usePolicy } from '../hooks'
import { AssetStatus } from '../types/Asset'
import { Edit } from '@mui/icons-material'
import { MouseEvent } from 'react'
import AssetLevelForm from './AssetLevelForm'

export interface AssetLevelEditButtonProps extends ButtonProps {
  asset: Asset
  component?: typeof IconButton | typeof Button
  afterUpdate?: (asset: Asset) => void
}

export default function AssetLevelEditButton({
  asset,
  component = IconButton,
  afterUpdate,
  children,
  onClick,
  ...props
}: AssetLevelEditButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The policy context.
   */
  const { canEditAsset } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle the click event.
   */
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event)
    openDialog()
  }

  function handleAfterSubmit(asset: Asset) {
    afterUpdate?.(asset)
    closeDialog()
  }

  return (
    <>
      {asset.status !== AssetStatus.INACTIVE && canEditAsset(asset) && (
        <>
          {component === IconButton ? (
            <Tooltip title={t('edit')}>
              <IconButton {...props} onClick={handleClick}>
                {children ?? <Edit />}
              </IconButton>
            </Tooltip>
          ) : (
            <Button {...props} onClick={handleClick}>
              {(children ?? asset?.levelId === null) ? t('addLevel') : t('editLevel')}
            </Button>
          )}
        </>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{asset?.levelId === null ? t('addLevel') : t('editLevel')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <AssetLevelForm asset={asset} afterSubmit={handleAfterSubmit} onCancel={closeDialog} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
